import { createSlice } from "@reduxjs/toolkit";
import {
  getInvoices,
  saveInvoice,
  updateInvoiceThunk,
  removeInvoice,
  uploadAndProcessInvoice,
  getRecurringExpenses,
  addRecurringExpense,
  editRecurringExpense,
  removeRecurringExpense,
} from "./thunks";

const initialState = {
  invoices: [],
  processingStatus: "idle", // idle | uploading | processing_ocr | analyzing | done | error
  processingError: null,
  ocrResult: null,
  status: "idle", // idle | loading | successful | failed
  error: null,
  recurringExpenses: [],
  recurringStatus: "idle", // idle | loading | successful | failed
};

export const accountingSlice = createSlice({
  name: "accounting",
  initialState,
  reducers: {
    resetProcessing: (state) => {
      state.processingStatus = "idle";
      state.processingError = null;
      state.ocrResult = null;
    },
    setProcessingStatus: (state, action) => {
      state.processingStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch invoices
    builder.addCase(getInvoices.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(getInvoices.fulfilled, (state, action) => {
      state.invoices = action.payload || [];
      state.status = "successful";
    });
    builder.addCase(getInvoices.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.error;
    });

    // Upload and process
    builder.addCase(uploadAndProcessInvoice.pending, (state) => {
      state.processingStatus = "uploading";
      state.processingError = null;
      state.ocrResult = null;
    });
    builder.addCase(uploadAndProcessInvoice.fulfilled, (state, action) => {
      state.processingStatus = "done";
      state.ocrResult = action.payload;
    });
    builder.addCase(uploadAndProcessInvoice.rejected, (state, action) => {
      state.processingStatus = "error";
      state.processingError = action.payload?.error || "Error processing invoice";
    });

    // Save invoice
    builder.addCase(saveInvoice.fulfilled, (state, action) => {
      state.invoices = [action.payload, ...state.invoices];
    });

    // Update invoice
    builder.addCase(updateInvoiceThunk.fulfilled, (state, action) => {
      state.invoices = state.invoices.map((inv) =>
        inv.id === action.payload.id ? action.payload : inv
      );
    });

    // Remove invoice
    builder.addCase(removeInvoice.fulfilled, (state, action) => {
      state.invoices = state.invoices.filter((inv) => inv.id !== action.payload);
    });

    // Fetch recurring expenses
    builder.addCase(getRecurringExpenses.pending, (state) => {
      state.recurringStatus = "loading";
    });
    builder.addCase(getRecurringExpenses.fulfilled, (state, action) => {
      state.recurringExpenses = action.payload;
      state.recurringStatus = "successful";
    });
    builder.addCase(getRecurringExpenses.rejected, (state, action) => {
      state.recurringStatus = "failed";
      state.error = action.error;
    });

    // Save recurring expense
    builder.addCase(addRecurringExpense.fulfilled, (state, action) => {
      if (action.payload) {
        state.recurringExpenses = [action.payload, ...state.recurringExpenses];
      }
    });

    // Update recurring expense
    builder.addCase(editRecurringExpense.fulfilled, (state, action) => {
      if (action.payload?.id) {
        state.recurringExpenses = state.recurringExpenses.map((expense) =>
          expense.id === action.payload.id ? action.payload : expense
        );
      }
    });

    // Remove recurring expense
    builder.addCase(removeRecurringExpense.fulfilled, (state, action) => {
      state.recurringExpenses = state.recurringExpenses.filter((expense) => expense.id !== action.payload);
    });
  },
});

// Selectors
export const invoicesSelector = (state) => state.accounting.invoices;
export const processingStatusSelector = (state) => state.accounting.processingStatus;
export const ocrResultSelector = (state) => state.accounting.ocrResult;
export const accountingStatusSelector = (state) => state.accounting.status;
export const recurringExpensesSelector = (state) => state.accounting.recurringExpenses;
export const recurringStatusSelector = (state) => state.accounting.recurringStatus;

export const { resetProcessing, setProcessingStatus } = accountingSlice.actions;

export default accountingSlice.reducer;
